import { LODGE_EMAIL, LODGE_EMAIL_HREF, LODGE_PHONE_DISPLAY, LODGE_PHONE_HREF, callAriaLabel } from "@/lib/contact-info";
import { Link } from "@tanstack/react-router";
import {
  Anchor,
  Mail,
  MapPin,
  Phone,
  Fish,
  Waves,
  Clock,
  Navigation,
  Facebook,
  Instagram,
  Youtube,
} from "lucide-react";
import logoAsset from "@/assets/coffman-cove-logo.png.asset.json";
import { NewsletterForm } from "./NewsletterForm";
import { LodgeHoursCard } from "./LodgeHours";

const socialLinks = [
  {
    label: "Facebook",
    href: "https://www.facebook.com/CoffmanCoveLodging",
    Icon: Facebook,
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/coffmancovelodging",
    Icon: Instagram,
  },
  {
    label: "YouTube",
    href: "https://www.youtube.com/@coffmancovelodging",
    Icon: Youtube,
  },
] as const;

const exploreLinks = [
  { to: "/accommodations", label: "Stay & Rentals" },
  { to: "/fishing", label: "Fishing" },
  { to: "/fishing-report", label: "Fishing Report" },
  { to: "/gallery", label: "Gallery" },
  { to: "/about", label: "Our Story" },
  { to: "/shop", label: "Shop" },
  { to: "/booking", label: "Book a Trip" },
] as const;

const tripLinks = [
  { to: "/contact", label: "Contact", ariaLabel: "Contact the lodge" },
  { to: "/booking", label: "Reserve Dates", ariaLabel: "Reserve trip dates" },
  { to: "/fishing-report", label: "Today's Conditions", ariaLabel: "View today's fishing conditions" },
  { to: "/my-stay", label: "My Stay", ariaLabel: "Look up your confirmed stay" },
] as const;

// Shared focus-visible ring — keyboard users get a clearly visible focus state
// on every interactive surface in the footer.
const focusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--electric)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--navy-deep)] rounded-full";

export function SiteFooter() {
  const year = new Date().getFullYear();

  return (
    <footer
      aria-labelledby="site-footer-heading"
      className="relative mt-24 overflow-hidden bg-[var(--navy-deep)] text-[var(--offwhite)]"
    >
      <h2 id="site-footer-heading" className="sr-only">
        Coffman Cove Lodging site footer
      </h2>

      {/* Top accent rule */}
      <div aria-hidden className="h-px w-full bg-gradient-to-r from-transparent via-[var(--electric)]/60 to-transparent" />

      {/* Decorative glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 left-1/2 h-72 w-[60rem] -translate-x-1/2 rounded-full opacity-30 blur-3xl"
        style={{ background: "radial-gradient(closest-side, var(--electric), transparent 70%)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, var(--offwhite) 1px, transparent 0)",
          backgroundSize: "22px 22px",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-5 pt-12 pb-8 sm:px-6 sm:pt-16 sm:pb-10">
        <div className="grid gap-10 sm:gap-12 md:grid-cols-12">
          {/* Brand block */}
          <div className="md:col-span-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="rounded-full ring-1 ring-[var(--electric)]/40 shadow-[var(--shadow-electric)]">
                <img
                  src={logoAsset.url}
                  alt=""
                  aria-hidden
                  className="h-16 w-16 rounded-full object-cover"
                />
              </div>
              <div>
                <p className="font-display text-2xl leading-tight">Coffman Cove Lodging</p>
                <p className="mt-1 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[var(--electric)]">
                  <Waves aria-hidden className="h-3.5 w-3.5" /> Southeast Alaska · Est. 1984
                </p>
              </div>
            </div>

            <p className="mt-6 max-w-md text-sm leading-relaxed text-[var(--offwhite-warm)]">
              Family owned and operated for more than 40 years on Prince of Wales Island.
              Lodging, meals, and boat rentals built around one idea — affordable fishing,
              the way it should be.
            </p>

            <p className="mt-6 inline-flex items-center gap-2 rounded-full border border-[var(--electric)]/30 bg-[var(--electric)]/10 px-3 py-1.5 text-xs font-medium text-[var(--electric)]">
              <Fish aria-hidden className="h-3.5 w-3.5" /> "It's all about the fun." — Jerry Hedges
            </p>

            {/* Social links */}
            <nav aria-label="Social media" className="mt-6">
              <p
                id="footer-social-heading"
                className="mb-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--electric)]"
              >
                Follow the Lodge
              </p>
              <ul aria-labelledby="footer-social-heading" className="flex flex-wrap items-center gap-2">
                {socialLinks.map(({ label, href, Icon }) => (
                  <li key={label}>
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Coffman Cove Lodging on ${label} (opens in a new tab)`}
                      className={`group inline-flex min-h-11 min-w-11 items-center justify-center rounded-full border border-[var(--electric)]/25 bg-[var(--navy)]/50 text-[var(--offwhite)] transition-all hover:-translate-y-0.5 hover:border-[var(--electric)] hover:bg-[var(--electric)] hover:text-[var(--electric-foreground)] hover:shadow-[var(--shadow-electric)] ${focusRing}`}
                    >
                      <Icon className="h-4.5 w-4.5" aria-hidden />
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Newsletter */}
            <section aria-labelledby="footer-newsletter-heading" className="mt-8">
              <h3
                id="footer-newsletter-heading"
                className="mb-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--electric)]"
              >
                Catch of the Week
              </h3>
              <p className="mb-3 text-xs leading-relaxed text-[var(--offwhite-warm)]">
                Tides, trip openings, and Coffman Cove fishing reports — straight to your inbox.
              </p>
              <NewsletterForm />
            </section>
          </div>

          {/* Explore */}
          <nav aria-labelledby="footer-explore-heading" className="md:col-span-2">
            <h3
              id="footer-explore-heading"
              className="mb-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--electric)]"
            >
              Explore
            </h3>
            <ul className="space-y-2.5 text-sm">
              {exploreLinks.map((link) => (
                <li key={link.label}>
                  {"href" in link ? (
                    <a
                      href={(link as { href: string }).href}
                      className={`group inline-flex items-center gap-2 text-[var(--offwhite)] transition-colors hover:text-[var(--electric)] ${focusRing}`}
                    >
                      <span
                        aria-hidden
                        className="h-px w-3 bg-[var(--electric)]/60 transition-all group-hover:w-6 group-hover:bg-[var(--electric)]"
                      />
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      to={link.to}
                      className={`group inline-flex items-center gap-2 text-[var(--offwhite)] transition-colors hover:text-[var(--electric)] ${focusRing}`}
                    >
                      <span
                        aria-hidden
                        className="h-px w-3 bg-[var(--electric)]/60 transition-all group-hover:w-6 group-hover:bg-[var(--electric)]"
                      />
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          {/* Contact */}
          <section aria-labelledby="footer-contact-heading" className="md:col-span-3">
            <h3
              id="footer-contact-heading"
              className="mb-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--electric)]"
            >
              Contact
            </h3>
            <ul className="space-y-3 text-sm text-[var(--offwhite)]">
              <li className="flex items-start gap-3">
                <MapPin aria-hidden className="mt-0.5 h-4 w-4 flex-none text-[var(--electric)]" />
                <address className="not-italic leading-snug">
                  306 Harbor Ave
                  <br />
                  Coffman Cove, AK 99918
                  <br />
                  <span className="text-[var(--offwhite-warm)]">Prince of Wales Island</span>
                </address>
              </li>
              <li>
                <a
                  href={LODGE_PHONE_HREF}
                  className={`inline-flex min-h-11 items-center gap-3 transition-colors hover:text-[var(--electric)] ${focusRing}`}
                  aria-label={callAriaLabel(LODGE_PHONE_DISPLAY)}
                >
                  <Phone aria-hidden className="h-4 w-4 text-[var(--electric)]" />
                  {LODGE_PHONE_DISPLAY}
                </a>
              </li>
              <li>
                <a
                  href={LODGE_EMAIL_HREF}
                  aria-label={`Email Coffman Cove Lodging at ${LODGE_EMAIL}`}
                  className={`inline-flex min-h-11 items-center gap-3 break-all transition-colors hover:text-[var(--electric)] ${focusRing}`}
                >
                  <Mail aria-hidden className="h-4 w-4 flex-none text-[var(--electric)]" />
                  {LODGE_EMAIL}
                </a>
              </li>

              <li>
                <a
                  href="https://maps.google.com/?q=Coffman+Cove+Lodging+Alaska"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get directions to Coffman Cove Lodging (opens in a new tab)"
                  className={`inline-flex min-h-11 items-center gap-3 transition-colors hover:text-[var(--electric)] ${focusRing}`}
                >
                  <Navigation aria-hidden className="h-4 w-4 text-[var(--electric)]" />
                  Get directions
                </a>
              </li>
            </ul>

            <div className="mt-5 grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
              {tripLinks.map((link) => {
                const className = `inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full bg-[var(--electric)] px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--electric-foreground)] shadow-[var(--shadow-electric)] transition-transform hover:-translate-y-0.5 sm:w-auto ${focusRing}`;
                return "href" in link ? (
                  <a
                    key={link.label}
                    href={(link as { href: string }).href}
                    aria-label={link.ariaLabel}
                    className={className}
                  >
                    <Anchor aria-hidden className="h-3.5 w-3.5" />
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.to}
                    to={link.to}
                    aria-label={link.ariaLabel}
                    className={className}
                  >
                    <Anchor aria-hidden className="h-3.5 w-3.5" />
                    {link.label}
                  </Link>
                );
              })}
            </div>
          </section>

          {/* Hours */}
          <section aria-labelledby="footer-hours-heading" className="md:col-span-3">
            <h3
              id="footer-hours-heading"
              className="mb-4 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--electric)]"
            >
              <Clock aria-hidden className="h-3.5 w-3.5" /> Lodge Hours
            </h3>
            <LodgeHoursCard variant="dark" />



          </section>
        </div>
      </div>

      {/* Policy notes */}
      <div className="relative border-t border-[var(--electric)]/10 bg-[var(--navy-deep)]/60">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-center gap-x-6 gap-y-2 px-5 py-4 text-center text-xs uppercase tracking-[0.18em] text-[var(--offwhite-warm)] sm:flex-row sm:flex-wrap sm:px-6">
          <span>Gratuities appreciated but not included</span>
          <span aria-hidden className="hidden text-[var(--electric)]/40 sm:inline">·</span>
          <span>We reserve the right to refuse service</span>
          <span aria-hidden className="hidden text-[var(--electric)]/40 sm:inline">·</span>
          <span>*Prices are subject to change</span>
        </div>
      </div>




      {/* Bottom bar */}
      <div className="relative border-t border-[var(--electric)]/15 bg-[var(--navy)]/40 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-5 py-5 text-xs text-[var(--offwhite-warm)] sm:px-6 md:flex-row md:items-center md:justify-between">
          <p className="leading-relaxed">
            © {year} Coffman Cove Lodging · Family owned &amp; operated since 1984.
          </p>
          <nav
            aria-label="Legal"
            className="-mx-2 flex flex-wrap items-center gap-y-1 md:mx-0"
          >
            <Link
              to="/privacy"
              className={`inline-flex min-h-11 items-center px-2 text-[var(--offwhite-warm)] hover:text-[var(--electric)] ${focusRing}`}
            >
              Privacy Policy
            </Link>
            <span aria-hidden className="text-[var(--electric)]/40">·</span>
            <Link
              to="/terms"
              className={`inline-flex min-h-11 items-center px-2 text-[var(--offwhite-warm)] hover:text-[var(--electric)] ${focusRing}`}
            >
              Terms of Service
            </Link>
            <span aria-hidden className="text-[var(--electric)]/40">·</span>
            <a
              href="/sitemap.xml"
              className={`inline-flex min-h-11 items-center px-2 text-[var(--offwhite-warm)] hover:text-[var(--electric)] ${focusRing}`}
            >
              Sitemap
            </a>
            <span
              aria-hidden
              className="hidden text-[var(--electric)]/40 sm:inline"
            >
              ·
            </span>
            <span className="inline-flex min-h-11 w-full items-center gap-2 px-2 text-[var(--offwhite-warm)] sm:w-auto">
              <span
                aria-hidden
                className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--electric)] shadow-[0_0_10px_var(--electric)]"
              />
              Live from Coffman Cove, AK
            </span>
          </nav>
        </div>
      </div>
    </footer>
  );
}
